import type { CopilotContextT } from "@malfak/contracts";
import { useEffect, useSyncExternalStore } from "react";

type EntityType = NonNullable<NonNullable<CopilotContextT["entity"]>["type"]>;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Route patterns that name one entity ("here", "this post"). Order matters: specific before generic. */
const ENTITY_ROUTES: [RegExp, EntityType][] = [
  [/^\/content\/posts\/([^/]+)/, "post"],
  [/^\/people\/([^/]+)/, "contact"],
  [/^\/contacts\/([^/]+)/, "contact"],
  [/^\/dashboards\/account\/([^/]+)/, "account"],
  [/^\/dashboards\/([^/]+)/, "dashboard"],
  [/^\/automation\/rules\/([^/]+)/, "rule"],
  [/^\/jobs\/([^/]+)/, "job"],
];
/** Page filters worth telling the agent about (strings only, trimmed). */
const FILTER_KEYS = ["state", "type", "sentiment", "intent", "assignee", "q", "flag", "accountId", "status", "mark", "sort"];

// ---------------------------------------------------------------- selection (pages publish what's selected)
type Selection = NonNullable<CopilotContextT["selection"]>;
let selection: Selection | null = null;
const ls = new Set<() => void>();
/** Pages with multi-select (inbox, post comments, people) call this so "reply to these" means the selection. */
export const copilotSelection = {
  get: () => selection,
  set(next: Selection | null) {
    selection = next && next.ids.length ? { type: next.type, ids: next.ids.slice(0, 200) } : null;
    ls.forEach((l) => l());
  },
  subscribe(l: () => void) {
    ls.add(l);
    return () => ls.delete(l);
  },
};
export const useCopilotSelection = () => useSyncExternalStore(copilotSelection.subscribe, copilotSelection.get, copilotSelection.get);

/**
 * ALP-80: a page with multi-select publishes it to the Orb ("hide these" targets exactly this selection). Pass the
 * selected ids (any iterable); an empty selection clears it, and leaving the page clears it too.
 */
export function usePublishSelection(type: Selection["type"], ids: Iterable<string>) {
  const list = [...ids];
  const key = `${type}:${list.join(",")}`;
  useEffect(() => {
    copilotSelection.set(list.length ? { type, ids: list } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => () => copilotSelection.set(null), []);
}

/** i18n key of a selection chip by entity type ("12 comments selected"); count-neutral wording. */
export const selectionKey = (type: Selection["type"]) => `copilot.selection.${["comment", "conversation", "contact", "approval"].includes(type) ? type : "other"}`;

/**
 * What the user is looking at, sent with every message: the route path, the entity on entity pages (a post page
 * → `{ type: "post", id }` so "hide the spam here" means this post), the inbox item in focus, the selection and
 * the visible filters.
 */
export function buildContext(input: { pathname: string; search?: Record<string, unknown>; title?: string; selection?: Selection | null; locale?: "en" | "ar"; timezone?: string }): CopilotContextT {
  const page = input.pathname.slice(0, 300) || "/";
  let entity: CopilotContextT["entity"] = null;
  for (const [re, type] of ENTITY_ROUTES) {
    const m = re.exec(page);
    if (m && UUID.test(m[1]!)) {
      entity = { type, id: m[1]! };
      break;
    }
  }
  const search = input.search ?? {};
  if (!entity && page.startsWith("/inbox") && typeof search.id === "string" && UUID.test(search.id)) {
    entity = { type: search.type === "conversation" ? "conversation" : "comment", id: search.id };
  }
  const filters: Record<string, string> = {};
  for (const k of FILTER_KEYS) {
    const v = search[k];
    if ((typeof v === "string" || typeof v === "number" || typeof v === "boolean") && String(v).trim()) filters[k] = String(v).slice(0, 200);
  }
  return {
    page,
    ...(input.title ? { title: input.title.slice(0, 200) } : {}),
    entity,
    selection: input.selection ?? null,
    ...(Object.keys(filters).length ? { filters } : {}),
    ...(input.locale ? { locale: input.locale } : {}),
    ...(input.timezone ? { timezone: input.timezone.slice(0, 64) } : {}),
  };
}

/** The i18n key describing the context chip ("this post", "3 selected", "this page"). */
export function contextLabel(ctx: CopilotContextT | null): { key: string; count?: number } | null {
  if (!ctx) return null;
  if (ctx.selection?.ids.length) return { key: "copilot.context.selection", count: ctx.selection.ids.length };
  if (ctx.entity) return { key: `copilot.context.${ctx.entity.type}` };
  return null;
}

/** Suggestion chips before the first message, by where the user is. */
export function defaultSuggestionKeys(ctx: CopilotContextT | null): string[] {
  const p = ctx?.page ?? "/";
  if (ctx?.entity?.type === "post") return ["postSpam", "postSummary", "postReload", "negativeWeek"];
  if (p.startsWith("/inbox")) return ["inboxPrices", "negativeWeek", "inboxOpenDms", "spam"];
  if (p.startsWith("/dashboards")) return ["dashboardAdd", "topPost", "bestTime", "negativeWeek"];
  if (p.startsWith("/people")) return ["harmful", "spam", "negativeWeek", "deleted"];
  if (p.startsWith("/jobs")) return ["syncHealth", "deleted", "negativeWeek", "spam"];
  return ["negativeWeek", "spam", "bestTime", "topPost", "deleted", "harmful"];
}
